import axios from 'axios';
import networkService from './networkService';

// Fonction pour obtenir l'URL de base du backend
export const getBackendBaseUrl = () => {
  // Utiliser REACT_APP_API_URL si défini (pour Docker/production)
  if (process.env.REACT_APP_API_URL) {
    // Si c'est un chemin relatif (commence par /), retourner vide pour utiliser l'origine
    if (process.env.REACT_APP_API_URL.startsWith('/')) {
      return '';
    }
    // Sinon, utiliser l'URL complète fournie
    return process.env.REACT_APP_API_URL.replace('/api', '');
  }
  
  // En production, le backend sera accessible via la même origine que le frontend
  // En développement, nous utilisons localhost:3001
  return process.env.NODE_ENV === 'production' 
    ? '' // En production, utiliser des chemins relatifs
    : 'http://localhost:3001';
};

// Créer une instance axios avec une configuration de base
const apiBaseURL = process.env.REACT_APP_API_URL 
  ? process.env.REACT_APP_API_URL 
  : `${getBackendBaseUrl()}/api`;

const api = axios.create({
  baseURL: apiBaseURL,
  headers: {
    'Content-Type': 'application/json'
  }
});

console.log('API configurée avec baseURL:', apiBaseURL);

// Intercepteur pour ajouter le token d'authentification aux requêtes
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    
    console.log('=== Requête API ===');
    console.log('URL:', config.url);
    console.log('Méthode:', config.method);
    console.log('Headers initiaux:', config.headers);
    
    if (token) {
      console.log('Token JWT trouvé, ajout à l\'en-tête');
      config.headers['x-auth-token'] = token;
    } else {
      console.warn('Aucun token JWT trouvé dans le localStorage');
    }
    
    console.log('Headers mis à jour:', config.headers);
    return config;
  },
  (error) => {
    console.error('Erreur dans l\'intercepteur de requête:', error);
    return Promise.reject(error);
  }
);

// Services d'authentification
export const authService = {
  // Inscription d'un nouvel utilisateur
  register: async (userData) => {
    try {
      const response = await api.post('/users', userData);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : new Error('Erreur de serveur');
    }
  },
  
  // Connexion d'un utilisateur
  login: async (credentials) => {
    try {
      const response = await api.post('/auth/login', credentials);
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : new Error('Erreur de serveur');
    }
  },
  
  // Déconnexion d'un utilisateur
  logout: () => {
    localStorage.removeItem('token');
  },
  
  // Récupérer les informations de l'utilisateur connecté
  getCurrentUser: async () => {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      // Si le serveur retourne du HTML au lieu de JSON (erreur 500)
      if (error.response && error.response.headers && error.response.headers['content-type']?.includes('text/html')) {
        console.error('Le serveur a retourné une erreur HTML. Vérifiez les logs du backend.');
        throw new Error('Erreur serveur: le backend a retourné une réponse invalide');
      }
      throw error.response ? error.response.data : new Error('Erreur de serveur');
    }
  },
  
  // Vérifier l'adresse email avec un token
  verifyEmail: async (token) => {
    try {
      const response = await api.get(`/auth/verify-email/${token}`);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : new Error('Erreur de serveur');
    }
  },
  
  // Renvoyer l'email de vérification
  resendVerificationEmail: async (email) => {
    try {
      const response = await api.post('/auth/resend-verification', { email });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : new Error('Erreur de serveur');
    }
  },
  
  // Demander une réinitialisation de mot de passe
  forgotPassword: async (email) => {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : new Error('Erreur de serveur');
    }
  },
  
  // Réinitialiser le mot de passe avec un token
  resetPassword: async (token, password) => {
    try {
      const response = await api.post(`/auth/reset-password/${token}`, { password });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : new Error('Erreur de serveur');
    }
  },
  
  // Supprimer complètement les données d'un utilisateur (conformité Loi 25 du Québec)
  deleteUserCompleteData: async (userId) => {
    try {
      const response = await api.delete(`/users/${userId}/complete`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression complète des données:', error);
      throw error;
    }
  }
};

// Services de profil
export const profileService = {
  // Télécharger un CV
  downloadCV: async (cvPath) => {
    try {
      console.log('Téléchargement du CV avec le chemin:', cvPath);
      
      // Extraire l'ID du profil et le nom du fichier du chemin
      const response = await axios({
        url: `${getBackendBaseUrl()}/api/profiles/download-cv?path=${encodeURIComponent(cvPath)}`,
        method: 'GET',
        responseType: 'blob',
        headers: {
          'x-auth-token': localStorage.getItem('token')
        }
      });
      
      // Vérifier que la réponse est bien un blob
      if (!(response.data instanceof Blob)) {
        console.error('La réponse n\'est pas un blob:', response.data);
        throw new Error('Format de réponse invalide');
      }
      
      // Créer un URL pour le blob et déclencher le téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extraire le nom du fichier du chemin ou utiliser un nom par défaut
      const fileName = cvPath.split('/').pop() || 'cv.pdf';
      link.setAttribute('download', fileName);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('Téléchargement du CV réussi');
      return true;
    } catch (error) {
      console.error('Erreur lors du téléchargement du CV:', error);
      
      // Gestion plus précise des erreurs
      if (error.response) {
        // La requête a été faite et le serveur a répondu avec un code d'état
        // qui ne fait pas partie de la plage 2xx
        console.error('Erreur de réponse:', error.response.status, error.response.data);
        alert(`Erreur lors du téléchargement du CV: ${error.response.status} ${error.response.statusText}`);
      } else if (error.request) {
        // La requête a été faite mais aucune réponse n'a été reçue
        console.error('Pas de réponse reçue:', error.request);
        alert('Aucune réponse du serveur. Vérifiez votre connexion internet.');
      } else {
        // Une erreur s'est produite lors de la configuration de la requête
        console.error('Erreur de configuration:', error.message);
        alert(`Erreur lors du téléchargement: ${error.message}`);
      }
      
      throw error;
    }
  },
  
  // Télécharger un document
  downloadDocument: async (documentPath) => {
    try {
      console.log('Téléchargement du document avec le chemin:', documentPath);
      
      const response = await axios({
        url: `${getBackendBaseUrl()}/api/profiles/download-document?path=${encodeURIComponent(documentPath)}`,
        method: 'GET',
        responseType: 'blob',
        headers: {
          'x-auth-token': localStorage.getItem('token')
        }
      });
      
      // Vérifier que la réponse est bien un blob
      if (!(response.data instanceof Blob)) {
        console.error('La réponse n\'est pas un blob:', response.data);
        throw new Error('Format de réponse invalide');
      }
      
      // Créer un URL pour le blob et déclencher le téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extraire le nom du fichier du chemin ou utiliser un nom par défaut
      const fileName = documentPath.split('/').pop() || 'document.pdf';
      link.setAttribute('download', fileName);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('Téléchargement du document réussi');
      return true;
    } catch (error) {
      console.error('Erreur lors du téléchargement du document:', error);
      
      // Gestion plus précise des erreurs
      if (error.response) {
        // La requête a été faite et le serveur a répondu avec un code d'état
        // qui ne fait pas partie de la plage 2xx
        console.error('Erreur de réponse:', error.response.status, error.response.data);
        alert(`Erreur lors du téléchargement du document: ${error.response.status} ${error.response.statusText}`);
      } else if (error.request) {
        // La requête a été faite mais aucune réponse n'a été reçue
        console.error('Pas de réponse reçue:', error.request);
        alert('Aucune réponse du serveur. Vérifiez votre connexion internet.');
      } else {
        // Une erreur s'est produite lors de la configuration de la requête
        console.error('Erreur de configuration:', error.message);
        alert(`Erreur lors du téléchargement: ${error.message}`);
      }
      
      throw error;
    }
  },
  
  // Ajouter un document PDF
  uploadDocument: async (documentFile, name, description = '', profileId = null) => {
    try {
      console.log(`Téléversement d'un document PDF: ${name}`);
      
      const formData = new FormData();
      formData.append('document', documentFile);
      formData.append('name', name);
      if (description) {
        formData.append('description', description);
      }
      
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          'x-auth-token': localStorage.getItem('token')
        }
      };
      
      // Si aucun ID de profil n'est fourni, récupérer le profil de l'utilisateur actuel
      if (!profileId) {
        try {
          // Récupérer le profil de l'utilisateur actuel
          const response = await api.get('/auth/me');
          const currentUser = response.data;
          
          // Récupérer le profil associé à cet utilisateur
          const profileResponse = await api.get(`/profiles?userId=${currentUser._id}`);
          if (profileResponse.data && profileResponse.data.length > 0) {
            profileId = profileResponse.data[0]._id;
          } else {
            throw new Error('Profil utilisateur non trouvé');
          }
        } catch (error) {
          console.error('Erreur lors de la récupération du profil:', error);
          throw new Error('Impossible de récupérer le profil utilisateur');
        }
      }
      
      const response = await api.post(`/profiles/${profileId}/document`, formData, config);
      console.log('Document téléversé avec succès:', response.data);
      return response.data;
    } catch (error) {
      console.error('Erreur API uploadDocument:', error);
      if (error.response) {
        console.error('Détails de l\'erreur:', error.response.status, error.response.data);
        throw error.response.data;
      } else if (error.request) {
        console.error('Pas de réponse reçue:', error.request);
        throw new Error('Aucune réponse du serveur. Vérifiez votre connexion internet.');
      } else {
        console.error('Erreur de configuration:', error.message);
        throw new Error(`Erreur lors de la configuration de la requête: ${error.message}`);
      }
    }
  },
  
  // Ajouter un document externe (URL)
  addDocumentUrl: async (documentData, profileId = null) => {
    try {
      if (!documentData || !documentData.name || !documentData.url) {
        console.error('Données du document manquantes');
        throw new Error('Nom et URL du document requis');
      }
      
      console.log(`Ajout d'une URL de document: ${documentData.name}`);
      
      // Si aucun ID de profil n'est fourni, récupérer le profil de l'utilisateur actuel
      if (!profileId) {
        try {
          // Récupérer le profil de l'utilisateur actuel
          const response = await api.get('/auth/me');
          const currentUser = response.data;
          
          // Récupérer le profil associé à cet utilisateur
          const profileResponse = await api.get(`/profiles?userId=${currentUser._id}`);
          if (profileResponse.data && profileResponse.data.length > 0) {
            profileId = profileResponse.data[0]._id;
          } else {
            throw new Error('Profil utilisateur non trouvé');
          }
        } catch (error) {
          console.error('Erreur lors de la récupération du profil:', error);
          throw new Error('Impossible de récupérer le profil utilisateur');
        }
      }
      
      const response = await api.post(`/profiles/${profileId}/document-url`, documentData);
      console.log('URL du document ajoutée avec succès:', response.data);
      return response.data;
    } catch (error) {
      console.error('Erreur API addDocumentUrl:', error);
      if (error.response) {
        console.error('Détails de l\'erreur:', error.response.status, error.response.data);
        throw error.response.data;
      } else if (error.request) {
        console.error('Pas de réponse reçue:', error.request);
        throw new Error('Aucune réponse du serveur. Vérifiez votre connexion internet.');
      } else {
        console.error('Erreur de configuration:', error.message);
        throw new Error(`Erreur lors de la configuration de la requête: ${error.message}`);
      }
    }
  },
  
  // Supprimer un document
  deleteDocument: async (documentId, profileId = null) => {
    try {
      if (!documentId) {
        console.error('ID de document manquant');
        throw new Error('ID de document manquant');
      }
      
      console.log(`Suppression du document ${documentId}`);
      
      // Si aucun ID de profil n'est fourni, récupérer le profil de l'utilisateur actuel
      if (!profileId) {
        try {
          // Récupérer le profil de l'utilisateur actuel
          const response = await api.get('/auth/me');
          const currentUser = response.data;
          
          // Récupérer le profil associé à cet utilisateur
          const profileResponse = await api.get(`/profiles?userId=${currentUser._id}`);
          if (profileResponse.data && profileResponse.data.length > 0) {
            profileId = profileResponse.data[0]._id;
          } else {
            throw new Error('Profil utilisateur non trouvé');
          }
        } catch (error) {
          console.error('Erreur lors de la récupération du profil:', error);
          throw new Error('Impossible de récupérer le profil utilisateur');
        }
      }
      
      const response = await api.delete(`/profiles/${profileId}/document/${documentId}`);
      console.log('Document supprimé avec succès:', response.data);
      return response.data;
    } catch (error) {
      console.error('Erreur API deleteDocument:', error);
      if (error.response) {
        console.error('Détails de l\'erreur:', error.response.status, error.response.data);
        throw error.response.data;
      } else if (error.request) {
        console.error('Pas de réponse reçue:', error.request);
        throw new Error('Aucune réponse du serveur. Vérifiez votre connexion internet.');
      } else {
        console.error('Erreur de configuration:', error.message);
        throw new Error(`Erreur lors de la configuration de la requête: ${error.message}`);
      }
    }
  },
  // Récupérer tous les profils
  getProfiles: async () => {
    try {
      const response = await api.get('/profiles');
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : new Error('Erreur de serveur');
    }
  },
  
  // Récupérer un profil par son ID
  getProfileById: async (id) => {
    try {
      if (!id) {
        console.error('ID non valide fourni à getProfileById');
        throw new Error('ID non valide');
      }
      
      console.log(`Requête API pour récupérer le profil avec ID: ${id}`);
      
      // Commenté pour utiliser les vraies données du profil
      // if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_MOCK_API === 'true') {
      //   console.log('Mode de développement: simulation de réponse API');
      //   return {
      //     _id: id,
      //     firstName: 'Prénom',
      //     lastName: 'Nom',
      //     email: 'email@example.com',
      //     expertise: ['Océanographie', 'Biologie marine'],
      //   };
      // }
      
      const response = await api.get(`/profiles/${id}`);
      console.log('Réponse API profil:', response.data);
      return response.data;
    } catch (error) {
      console.error('Erreur API getProfileById:', error);
      if (error.response) {
        // Si c'est une erreur 404, on retourne null au lieu de lancer une exception
        if (error.response.status === 404) {
          console.log('Profil non trouvé (404), retour null');
          return null;
        }
        console.error('Détails de l\'erreur:', error.response.status, error.response.data);
        throw error.response.data;
      } else if (error.request) {
        console.error('Pas de réponse reçue:', error.request);
        throw new Error('Aucune réponse du serveur. Vérifiez votre connexion internet.');
      } else {
        console.error('Erreur de configuration:', error.message);
        throw new Error(`Erreur lors de la configuration de la requête: ${error.message}`);
      }
    }
  },
  
  // Récupérer un profil par l'ID de l'utilisateur
  getProfileByUserId: async (userId) => {
    try {
      if (!userId) {
        console.error('ID utilisateur non valide fourni à getProfileByUserId');
        throw new Error('ID utilisateur non valide');
      }
      
      console.log(`Requête API pour récupérer le profil de l'utilisateur avec ID: ${userId}`);
      
      // Utiliser la nouvelle route API dédiée pour récupérer le profil par l'ID de l'utilisateur
      const response = await api.get(`/profiles/user/${userId}`);
      console.log('Réponse API profil par userId:', response.data);
      return response.data;
    } catch (error) {
      console.error('Erreur API getProfileByUserId:', error);
      if (error.response) {
        // Si c'est une erreur 404, on retourne null au lieu de lancer une exception
        if (error.response.status === 404) {
          console.log('Profil non trouvé pour cet utilisateur (404), retour null');
          return null;
        }
        console.error('Détails de l\'erreur:', error.response.status, error.response.data);
        throw error.response.data;
      } else if (error.request) {
        console.error('Pas de réponse reçue:', error.request);
        throw new Error('Aucune réponse du serveur. Vérifiez votre connexion internet.');
      } else {
        console.error('Erreur de configuration:', error.message);
        throw new Error(`Erreur lors de la configuration de la requête: ${error.message}`);
      }
    }
  },
  
  // Rechercher des profils
  searchProfiles: async (params) => {
    try {
      const response = await api.get('/profiles/search', { params });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : new Error('Erreur de serveur');
    }
  },
  
  // Créer ou mettre à jour un profil
  createOrUpdateProfile: async (profileData) => {
    try {
      if (!profileData || (!profileData.user && !profileData.userId)) {
        console.error('Données de profil ou ID utilisateur manquants');
        throw new Error('Données de profil ou ID utilisateur manquants');
      }
      
      // Créer une copie profonde des données pour éviter de modifier l'objet d'origine
      const dataToSend = JSON.parse(JSON.stringify(profileData));
      
      // Assurer la compatibilité avec le schéma du backend qui attend 'user'
      if (dataToSend.userId && !dataToSend.user) {
        dataToSend.user = dataToSend.userId;
        delete dataToSend.userId;
      }
      
      // S'assurer que les champs de type tableau sont correctement formatés
      const arrayFields = ['titles', 'networks', 'websites', 'socialMedia', 'languages', 'datasets', 'projects', 'infrastructure'];
      
      // Gérer l'expertise séparément pour la rendre optionnelle
      if (dataToSend.expertise === undefined || dataToSend.expertise === null) {
        dataToSend.expertise = [];
      } else if (!Array.isArray(dataToSend.expertise)) {
        dataToSend.expertise = [dataToSend.expertise].filter(Boolean);
      }
      
      // Nettoyer les autres tableaux
      arrayFields.forEach(field => {
        if (!dataToSend[field] || !Array.isArray(dataToSend[field])) {
          dataToSend[field] = [];
        } else {
          // Nettoyer les entrées vides ou invalides
          dataToSend[field] = dataToSend[field]
            .filter(item => item !== null && typeof item === 'object')
            .map(item => {
              const cleanItem = {};
              // Ne conserver que les propriétés avec des valeurs non vides
              Object.entries(item).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                  cleanItem[key] = value;
                }
              });
              return cleanItem;
            });
        }
      });
      
      console.log('Données nettoyées pour l\'envoi:', JSON.stringify(dataToSend, null, 2));
      
      const response = await api.post('/profiles', dataToSend);
      console.log('Réponse API création/mise à jour de profil:', response.data);
      return response.data;
    } catch (error) {
      console.error('Erreur API createOrUpdateProfile:', error);
      if (error.response) {
        console.error('Détails de l\'erreur:', error.response.status, error.response.data);
        console.error('Headers de la réponse:', error.response.headers);
        // Afficher plus de détails sur l'erreur
        if (error.response.data && error.response.data.errors) {
          console.error('Erreurs de validation:', error.response.data.errors);
        }
        throw error.response.data || { message: `Erreur HTTP ${error.response.status}` };
      } else if (error.request) {
        console.error('Pas de réponse reçue:', error.request);
        throw new Error('Aucune réponse du serveur. Vérifiez votre connexion internet et que le backend est en cours d\'exécution.');
      } else {
        console.error('Erreur de configuration:', error.message);
        throw new Error(`Erreur lors de la configuration de la requête: ${error.message}`);
      }
    }
  },
  
  // Mettre à jour un profil
  updateProfile: async (id, profileData) => {
    try {
      const response = await api.put(`/profiles/${id}`, profileData);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : new Error('Erreur de serveur');
    }
  },
  
  // Téléverser une photo de profil
  uploadProfilePhoto: async (profileId, photoFile) => {
    try {
      if (!profileId || !photoFile) {
        console.error('ID de profil ou fichier photo manquant');
        throw new Error('ID de profil ou fichier photo manquant');
      }
      
      console.log(`Téléversement de photo pour le profil avec ID: ${profileId}`);
      
      // Créer un objet FormData pour envoyer le fichier
      const formData = new FormData();
      formData.append('photo', photoFile);
      
      // Configuration spéciale pour les requêtes multipart/form-data
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };
      
      const response = await api.post(`/profiles/${profileId}/photo`, formData, config);
      console.log('Photo téléversée avec succès:', response.data);
      return response.data;
    } catch (error) {
      console.error('Erreur API uploadProfilePhoto:', error);
      if (error.response) {
        console.error('Détails de l\'erreur:', error.response.status, error.response.data);
        throw error.response.data;
      } else if (error.request) {
        console.error('Pas de réponse reçue:', error.request);
        throw new Error('Aucune réponse du serveur. Vérifiez votre connexion internet.');
      } else {
        console.error('Erreur de configuration:', error.message);
        throw new Error(`Erreur lors de la configuration de la requête: ${error.message}`);
      }
    }
  },
  
  // Téléverser un CV au format PDF
  uploadCV: async (profileId, cvFile) => {
    try {
      if (!profileId || !cvFile) {
        console.error('ID de profil ou fichier CV manquant');
        throw new Error('ID de profil ou fichier CV manquant');
      }
      
      console.log(`Téléversement de CV pour le profil avec ID: ${profileId}`);
      
      // Créer un objet FormData pour envoyer le fichier
      const formData = new FormData();
      formData.append('cv', cvFile);
      
      // Configuration spéciale pour les requêtes multipart/form-data
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };
      
      const response = await api.post(`/profiles/${profileId}/cv`, formData, config);
      console.log('CV téléversé avec succès:', response.data);
      return response.data;
    } catch (error) {
      console.error('Erreur API uploadCV:', error);
      if (error.response) {
        console.error('Détails de l\'erreur:', error.response.status, error.response.data);
        throw error.response.data;
      } else if (error.request) {
        console.error('Pas de réponse reçue:', error.request);
        throw new Error('Aucune réponse du serveur. Vérifiez votre connexion internet.');
      } else {
        console.error('Erreur de configuration:', error.message);
        throw new Error(`Erreur lors de la configuration de la requête: ${error.message}`);
      }
    }
  },
  
  // Mettre à jour l'URL du CV
  updateCVUrl: async (profileId, url) => {
    try {
      if (!profileId || !url) {
        console.error('ID de profil ou URL manquante');
        throw new Error('ID de profil ou URL manquante');
      }
      
      console.log(`Mise à jour de l'URL du CV pour le profil avec ID: ${profileId}`);
      
      const response = await api.put(`/profiles/${profileId}/cv-url`, { url });
      console.log('URL du CV mise à jour avec succès:', response.data);
      return response.data;
    } catch (error) {
      console.error('Erreur API updateCVUrl:', error);
      if (error.response) {
        console.error('Détails de l\'erreur:', error.response.status, error.response.data);
        throw error.response.data;
      } else if (error.request) {
        console.error('Pas de réponse reçue:', error.request);
        throw new Error('Aucune réponse du serveur. Vérifiez votre connexion internet.');
      } else {
        console.error('Erreur de configuration:', error.message);
        throw new Error(`Erreur lors de la configuration de la requête: ${error.message}`);
      }
    }
  },
  
  // Exporter les données utilisateur (conformité Loi 25 du Québec)
  exportUserData: async (userId) => {
    try {
      if (!userId) {
        throw new Error('ID utilisateur requis pour exporter les données');
      }
      
      console.log(`Exportation des données pour l'utilisateur avec ID: ${userId}`);
      
      const response = await api.get(`/users/${userId}/export-data`, { responseType: 'blob' });
      return response.data;
    } catch (error) {
      console.error('Erreur API exportUserData:', error);
      if (error.response) {
        console.error('Détails de l\'erreur:', error.response.status, error.response.data);
        throw error.response.data;
      } else if (error.request) {
        console.error('Pas de réponse reçue:', error.request);
        throw new Error('Aucune réponse du serveur. Vérifiez votre connexion internet.');
      } else {
        console.error('Erreur de configuration:', error.message);
        throw new Error(`Erreur lors de la configuration de la requête: ${error.message}`);
      }
    }
  }
};

// Services utilisateur
export const userService = {
  // Récupérer tous les utilisateurs
  getUsers: async () => {
    try {
      const response = await api.get('/users');
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : new Error('Erreur de serveur');
    }
  },
  
  // Récupérer un utilisateur par son ID
  getUserById: async (userId) => {
    try {
      const response = await api.get(`/users/${userId}`);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : new Error('Erreur de serveur');
    }
  },
  
  // Supprimer un utilisateur
  deleteUser: async (userId) => {
    try {
      const response = await api.delete(`/users/${userId}`);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : new Error('Erreur de serveur');
    }
  },
  
  // Supprimer complètement les données d'un utilisateur (conformité Loi 25 du Québec)
  deleteUserCompleteData: async (userId) => {
    try {
      const response = await api.delete(`/users/${userId}/complete`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression complète des données:', error);
      throw error;
    }
  }
};

// Services d'administration
export const adminService = {
  // Récupérer les statistiques des utilisateurs (admin seulement)
  getUsersStats: async () => {
    try {
      const response = await api.get('/admin/users/stats');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques utilisateurs:', error);
      throw error.response ? error.response.data : new Error('Erreur lors de la récupération des statistiques utilisateurs');
    }
  },

  // Récupérer les statistiques des profils (admin seulement)
  getProfilesStats: async () => {
    try {
      const response = await api.get('/admin/profiles/stats');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques des profils:', error);
      throw error.response ? error.response.data : new Error('Erreur lors de la récupération des statistiques des profils');
    }
  },

  // Récupérer tous les utilisateurs (admin seulement)
  getAllUsers: async () => {
    try {
      const response = await api.get('/admin/users');
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : new Error('Erreur de serveur');
    }
  },
  
  // Récupérer tous les profils avec statut d'approbation (admin seulement)
  getAllProfiles: async () => {
    try {
      const response = await api.get('/admin/profiles');
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : new Error('Erreur de serveur');
    }
  },
  
  // Approuver un profil (admin seulement)
  approveProfile: async (profileId) => {
    try {
      const response = await api.put(`/admin/profiles/${profileId}/approve`);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : new Error('Erreur de serveur');
    }
  },
  
  // Rejeter un profil (admin seulement)
  rejectProfile: async (profileId, reason) => {
    try {
      const response = await api.put(`/admin/profiles/${profileId}/reject`, { reason });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : new Error('Erreur de serveur');
    }
  },
  
  // Modifier un profil (admin seulement)
  updateUserProfile: async (userId, profileData) => {
    try {
      const response = await api.put(`/admin/users/${userId}/profile`, profileData);
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : new Error('Erreur de serveur');
    }
  },
  
  // Changer le rôle d'un utilisateur (admin seulement)
  updateUserRole: async (userId, isAdmin) => {
    try {
      const response = await api.put(`/admin/users/${userId}/role`, { isAdmin });
      return response.data;
    } catch (error) {
      throw error.response ? error.response.data : new Error('Erreur de serveur');
    }
  }
};

// Service pour les organisations
export const organizationService = {
  // Récupérer toutes les organisations
  getOrganizations: async (params = {}) => {
    try {
      const response = await api.get('/organizations', { params });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des organisations:', error);
      throw error.response ? error.response.data : new Error('Erreur lors de la récupération des organisations');
    }
  },
  
  // Rechercher des organisations par nom
  searchOrganizations: async (query) => {
    try {
      const response = await api.get('/organizations/search', { params: { q: query } });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la recherche d\'organisations:', error);
      throw error.response ? error.response.data : new Error('Erreur lors de la recherche d\'organisations');
    }
  },
  
  // Créer une organisation
  createOrganization: async (organizationData) => {
    try {
      const response = await api.post('/organizations', organizationData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de l\'organisation:', error);
      throw error.response ? error.response.data : new Error('Erreur lors de la création de l\'organisation');
    }
  },
  
  // Mettre à jour une organisation
  updateOrganization: async (id, organizationData) => {
    try {
      const response = await api.put(`/organizations/${id}`, organizationData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'organisation:', error);
      throw error.response ? error.response.data : new Error('Erreur lors de la mise à jour de l\'organisation');
    }
  },
  
  // Supprimer une organisation
  deleteOrganization: async (id) => {
    try {
      const response = await api.delete(`/organizations/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'organisation:', error);
      throw error.response ? error.response.data : new Error('Erreur lors de la suppression de l\'organisation');
    }
  },
  
  // Récupérer une organisation par son ID
  getOrganizationById: async (id) => {
    try {
      const response = await api.get(`/organizations/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des détails de l\'organisation:', error);
      throw error.response ? error.response.data : new Error('Erreur lors de la récupération des détails de l\'organisation');
    }
  }
};

// Service pour les statistiques publiques
export const statsService = {
  // Récupérer les statistiques publiques
  getPublicStats: async () => {
    try {
      const response = await api.get('/public/stats');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques publiques:', error);
      throw error.response ? error.response.data : new Error('Erreur lors de la récupération des statistiques');
    }
  }
};

// Exporter le service de réseau
export { networkService };

export default api;
